import { Button } from '@base-ui/react/button';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { apiErrorDetail } from '@/admin/errorDetail';
import { QUESTION_TYPE_LABELS } from '@/admin/questionTypes';
import buttonStyles from '@/admin/styles/button.module.css';
import { Breadcrumbs } from '@/admin/ui/Breadcrumbs';
import { ItemBand, ItemVersionMenu } from '@/admin/ui/ContentNav';
import { DetailPage } from '@/admin/ui/Editor';
import { useEditMode, validateEditSearch } from '@/admin/ui/editSearch';
import { Field, FieldLabel, FieldValue } from '@/admin/ui/FieldDisplay';
import { PageTitle } from '@/admin/ui/Page';
import { finalQuizTitle, QuizBadge } from '@/admin/ui/QuizBadge';
import { LockedQuizNotice } from '@/admin/ui/QuizLock';
import { RichContent } from '@/admin/ui/RichContent';
import { FieldGrid, Section, SectionTitle } from '@/admin/ui/Section';
import { deletePrompt } from '@/admin/ui/Versions';
import {
  acceptedSpellings,
  clozeHtml,
  QuestionEditor,
} from '@/admin/widgets/QuestionEditor';
import api, { queryClient } from '@/api';
import type { components } from '@/api/qraft/schema';

// The admin read carries the answer key; the student `Question` shape doesn't.
type QuestionAdmin = components['schemas']['QuestionAdmin'];
type Answer = NonNullable<QuestionAdmin['answers']>[number];

export const Route = createFileRoute('/admin/questions/$questionId')({
  component: QuestionDetail,
  validateSearch: validateEditSearch,
});

function QuestionDetail() {
  const { t } = useTranslation();
  const { questionId } = Route.useParams();
  const questionIdNum = Number(questionId);
  const { edit } = Route.useSearch();
  const { editing, startEditing, stopEditing } = useEditMode(edit === true);

  const { data, isLoading, error } = api.adminGetQuestion.useQuery({
    path: { question_id: questionIdNum },
  });

  if (isLoading) return <Message>{t('admin.questions.loadingOne')}</Message>;
  if (error)
    return <Message $error>{t('admin.questions.errorLoadingOne')}</Message>;
  if (!data) return <Message $error>{t('admin.questions.notFound')}</Message>;

  // A draft in a quiz students may have can never be published, so it's view-only.
  const stuckDraft = data.quiz_locked && data.status !== 'published';

  return (
    <DetailPage>
      <ItemBand path={data.path} />
      <Breadcrumbs
        path={data.path}
        extra={[
          <Link
            key="quiz"
            to="/admin/questions/"
            search={{
              lesson: data.lesson_id,
              topic: data.topic_id ?? undefined,
            }}
          >
            {data.topic_id == null
              ? finalQuizTitle(
                  data.path.find((crumb) => crumb.kind === 'lesson')?.title,
                )
              : t('admin.quizBadge.topic')}
          </Link>,
        ]}
        current={t('admin.questions.questionN', {
          id: data.id,
          interpolation: { escapeValue: false },
        })}
      />
      <Header>
        <TitleRow>
          <PageTitle>
            {t('admin.questions.questionN', {
              id: data.id,
              interpolation: { escapeValue: false },
            })}
          </PageTitle>
          <QuizBadge final={data.topic_id == null} />
          <ItemVersionMenu
            path={data.path}
            entity="question"
            lineageId={data.lineage_id}
          />
        </TitleRow>
        {!editing && !stuckDraft && (
          <Button className={buttonStyles.Button} onClick={startEditing}>
            {t('admin.common.edit')}
          </Button>
        )}
      </Header>
      {stuckDraft && <StuckDraft data={data} />}
      {/* A stuck draft is delete-only, even when ?edit=true is in the URL. */}
      {editing && !stuckDraft ? (
        // key remounts the form per question, so its seeded state stays fresh.
        <QuestionEditor
          key={data.id}
          question={data}
          lessonId={data.lesson_id}
          topicId={data.topic_id ?? undefined}
          quizLocked={data.quiz_locked}
          onDone={stopEditing}
        />
      ) : (
        <QuestionView data={data} />
      )}
    </DetailPage>
  );
}

function StuckDraft({ data }: { data: QuestionAdmin }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const topic = data.topic_id ?? undefined;
  const deleteMutation = api.adminDeleteQuestion.useMutation(undefined, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: api.adminListQuestions.getQueryKey({
          path: { lesson_id: data.lesson_id },
        }),
      });
      navigate({
        to: '/admin/questions/',
        search: { lesson: data.lesson_id, topic },
      });
    },
  });

  const handleDelete = () => {
    if (!window.confirm(deletePrompt('question', data))) return;
    deleteMutation.mutate({ path: { question_id: data.id } });
  };

  return (
    <StuckDraftBar>
      <LockedQuizNotice parent={topic ? 'topic' : 'lesson'} draft />
      <Button
        className={buttonStyles.Button}
        onClick={handleDelete}
        disabled={deleteMutation.isPending}
      >
        {deleteMutation.isPending
          ? t('admin.common.deleting')
          : t('admin.common.delete')}
      </Button>
      {deleteMutation.isError && (
        <Message $error>
          {apiErrorDetail(deleteMutation.error) ??
            t('admin.questionEditor.deleteError')}
        </Message>
      )}
    </StuckDraftBar>
  );
}

function HtmlSection({ title, html }: { title: string; html: string }) {
  if (!html) return null;
  return (
    <Section>
      <SectionTitle>{title}</SectionTitle>
      <Body>
        {/* biome-ignore lint/security/noDangerouslySetInnerHtml: admin-authored HTML, trusted in admin */}
        <RichContent dangerouslySetInnerHTML={{ __html: html }} />
      </Body>
    </Section>
  );
}

function QuestionView({ data }: { data: QuestionAdmin }) {
  const { t } = useTranslation();
  const isCloze = data.type === 'fill_in_blank';
  const questionHtml = isCloze
    ? clozeHtml(
        data.cloze_replacer,
        (data.answers ?? []).map(acceptedSpellings),
        'answers',
      )
    : data.text;

  return (
    <>
      <Section>
        <SectionTitle>{t('admin.common.overview')}</SectionTitle>
        <FieldGrid>
          <Field>
            <FieldLabel>{t('admin.questionEditor.typeLabel')}</FieldLabel>
            <FieldValue>{t(QUESTION_TYPE_LABELS[data.type])}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.common.order')}</FieldLabel>
            <FieldValue>{data.order}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.questionEditor.points')}</FieldLabel>
            <FieldValue>{data.score}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.common.status')}</FieldLabel>
            <FieldValue>{data.status}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.topics.lesson')}</FieldLabel>
            <FieldValue>
              <Link
                to="/admin/lessons/$lessonId/"
                params={{ lessonId: String(data.lesson_id) }}
              >
                #{data.lesson_id}
              </Link>
            </FieldValue>
          </Field>
          {data.topic_id != null && (
            <Field>
              <FieldLabel>{t('admin.topics.panelLabel')}</FieldLabel>
              <FieldValue>
                <Link
                  to="/admin/topics/$topicId/"
                  params={{ topicId: String(data.topic_id) }}
                >
                  #{data.topic_id}
                </Link>
              </FieldValue>
            </Field>
          )}
        </FieldGrid>
      </Section>

      <Section>
        <SectionTitle>
          {isCloze
            ? t('admin.questions.questionCloze')
            : t('admin.questionEditor.question')}
        </SectionTitle>
        <Body>
          {questionHtml ? (
            <RichContent
              // biome-ignore lint/security/noDangerouslySetInnerHtml: admin-authored HTML, trusted in admin
              dangerouslySetInnerHTML={{ __html: questionHtml }}
            />
          ) : (
            <em>{t('admin.questionAnswer.noQuestionText')}</em>
          )}
        </Body>
      </Section>

      {!isCloze && data.answers && data.answers.length > 0 && (
        <Section>
          <SectionTitle>
            {data.disable_correct
              ? t('admin.questions.optionsNoCorrect')
              : t('admin.questionEditor.options')}
          </SectionTitle>
          {!data.disable_correct &&
            data.type !== 'essay' &&
            !data.answers.some((a) => a.is_correct) && (
              <NoCorrectWarning>
                {t('admin.questions.noCorrectWarning')}
              </NoCorrectWarning>
            )}
          <AnswerList>
            {data.answers.map((a, i) => (
              <AnswerView key={i} answer={a} />
            ))}
          </AnswerList>
        </Section>
      )}

      <HtmlSection title={t('admin.questionEditor.hint')} html={data.hint} />
      <HtmlSection
        title={t('admin.questionEditor.messageCorrect')}
        html={data.correct_msg}
      />
      <HtmlSection
        title={t('admin.questionEditor.messageIncorrect')}
        html={data.incorrect_msg}
      />
      <HtmlSection
        title={t('admin.questionEditor.explanation')}
        html={data.explanation}
      />
    </>
  );
}

function AnswerView({ answer }: { answer: Answer }) {
  const { t } = useTranslation();
  return (
    <AnswerRow $correct={answer.is_correct}>
      <AnswerMark>{answer.is_correct ? '✓' : ''}</AnswerMark>
      <RichContent
        // biome-ignore lint/security/noDangerouslySetInnerHtml: admin-authored HTML, trusted in admin
        dangerouslySetInnerHTML={{ __html: answer.text }}
      />
      {answer.is_correct && (
        <small>{t('admin.questionAnswer.choiceCorrect')}</small>
      )}
    </AnswerRow>
  );
}

const Header = styled.div`
  align-items: center;
  display: flex;
  gap: var(--component-margin);
  justify-content: space-between;
`;

const TitleRow = styled.div`
  align-items: center;
  display: flex;
  gap: 0.75rem;
`;

const StuckDraftBar = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: var(--component-margin);
`;

const Body = styled.div`
  padding: var(--inner-padding);
`;

const AnswerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: var(--inner-padding);
`;

const AnswerRow = styled.div<{ $correct: boolean }>`
  align-items: center;
  background: ${(p) =>
    p.$correct
      ? 'color-mix(in srgb, var(--success) 14%, transparent)'
      : 'transparent'};
  border-left: 3px solid
    ${(p) => (p.$correct ? 'var(--success)' : 'transparent')};
  border-radius: 4px;
  color: var(--grey-700);
  display: grid;
  gap: 0.5rem;
  grid-template-columns: 1.5rem 1fr auto;
  padding: 0.5rem 0.75rem;
`;

const NoCorrectWarning = styled.p`
  background: color-mix(in srgb, var(--orange) 12%, transparent);
  border-left: 3px solid var(--orange);
  border-radius: 0.25rem;
  color: var(--grey-800);
  font-size: 0.8125rem;
  line-height: 1.4;
  margin: 0 1rem 0.5rem;
  padding: 0.5rem 0.75rem;
`;

const AnswerMark = styled.span`
  color: var(--success);
  font-weight: 700;
  text-align: center;
`;

const Message = styled.div<{ $error?: boolean }>`
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
  padding: 2rem;
  text-align: center;
`;
